import { useState, useRef, useEffect } from 'react';
import Modal from '../Modal';
import { useApi } from '../../contexts/ApiContext';
import { useToast } from '../../contexts/ToastContext';

interface Message {
    id: number;
    message: string;
    image_data: string | null;
    created_at: string;
    created_by_user?: { username: string };
}

interface Ticket {
    id: number;
    created_at: string;
    target_date: string;
    order_number: string;
    reason: string;
    responsible: string;
    priority: string;
    description: string;
    status: string;
    image_data: string | null;
    created_by_user?: { username: string };
    messages: Message[];
    notified_at: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  ticketId: number | null;
  onUpdate: () => void;
}

export default function TicketDetailModal({ isOpen, onClose, ticketId, onUpdate }: Props) {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  const [newMessage, setNewMessage] = useState('');
  const [newImage, setNewImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && ticketId) {
        loadTicket();
    } else {
        setTicket(null);
    }
  }, [isOpen, ticketId]);

  const loadTicket = async () => {
    try {
        setLoading(true);
        const res = await api.get(`/support/${ticketId}`);
        setTicket(res.data);
    } catch (err: any) {
        showError('Error cargando ticket');
        onClose();
    } finally {
        setLoading(false);
    }
  };

  const handleImageUpload = (file: File) => {
    if (file.size > 5 * 1024 * 1024) { 
        showError('La imagen es demasiado grande (max 5MB)');
        return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setNewImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const onPaste = (e: React.ClipboardEvent) => {
    if (e.clipboardData.files.length > 0) {
      handleImageUpload(e.clipboardData.files[0]);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage && !newImage) return;

    try {
        setSending(true);
        await api.post(`/support/${ticketId}/messages`, {
            message: newMessage,
            image_data: newImage
        });
        setNewMessage('');
        setNewImage(null);
        await loadTicket();
        onUpdate();
        showSuccess('Mensaje enviado');
    } catch (err: any) {
        showError('Error enviando mensaje');
    } finally {
        setSending(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!window.confirm('¿Estás seguro de que quieres cerrar este ticket?')) return;
    try {
        await api.patch(`/support/${ticketId}/status`, { status: 'closed' });
        showSuccess('Ticket cerrado');
        onUpdate();
        onClose();
    } catch (err: any) {
        const errorMessage = err.response?.data?.error || err.message || 'Error cerrando ticket';
        showError(errorMessage);
        console.error('Error cerrando ticket:', err);
    }
  };

  const handleReopenTicket = async () => {
      try {
          await api.patch(`/support/${ticketId}/status`, { status: 'open' });
          showSuccess('Ticket reabierto');
          await loadTicket();
          onUpdate();
      } catch (err: any) {
          const errorMessage = err.response?.data?.error || err.message || 'Error reabriendo ticket';
          showError(errorMessage);
          console.error('Error reabriendo ticket:', err);
      }
  };

  const handleMoveToAmphora = async () => {
    try {
      await api.patch(`/support/${ticketId}/status`, { status: 'amphora' });
      showSuccess('Ticket movido a Amphora');
      await loadTicket();
      onUpdate();
    } catch (err: any) {
        const errorMessage = err.response?.data?.error || err.message || 'Error moviendo a Amphora';
        showError(errorMessage);
        console.error('Error moviendo a Amphora:', err);
    }
  };

  const handleMarkAsNotified = async () => {
      try {
          await api.post(`/support/${ticketId}/notified`);
          showSuccess('Ticket marcado como notificado');
          await loadTicket();
          onUpdate();
      } catch (err: any) {
          showError(err.response?.data?.error || 'Error marcando como notificado');
      }
  };

  const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'Urgent': return '#dc3545';
            case 'High': return '#ffc107';
            case 'Medium': return '#17a2b8';
            case 'Low': return '#28a745';
            default: return 'var(--text-primary)';
        }
    };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={ticket ? `Ticket #${ticket.id} - ${ticket.reason}` : 'Cargando ticket...'}
      size="large"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {ticket && (ticket.status === 'open' ? (
                    <>
                        <button className="btn btn-danger" onClick={handleCloseTicket}>Cerrar Ticket</button>
                        <button className="btn btn-secondary" style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6', color: 'white' }} onClick={handleMoveToAmphora}>Mover a Amphora</button>
                    </>
                ) : ticket?.status === 'amphora' ? (
                    <>
                        <button className="btn btn-warning" onClick={handleReopenTicket}>Reabrir a Soporte</button>
                        <button className="btn btn-danger" onClick={handleCloseTicket}>Cerrar Ticket</button>
                    </>
                ) : (
                    <>
                        <button className="btn btn-warning" onClick={handleReopenTicket}>Reabrir Ticket</button>
                        {!ticket?.notified_at && (
                            <button className="btn btn-success" onClick={handleMarkAsNotified}>
                                ✓ Marcar como Notificado
                            </button>
                        )}
                    </>
                ))}
            </div>
            <button className="btn btn-secondary" onClick={onClose}>Cerrar Ventana</button>
        </div>
      }
    >
      {loading && !ticket ? (
        <div style={{ padding: '2rem', textAlign: 'center' }}>Cargando ticket...</div>
      ) : ticket ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Header Info */}
        <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '1rem', borderRadius: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
                <p><strong>Estado:</strong> <span className={`badge ${ticket.status === 'open' ? 'badge-success' : ticket.status === 'amphora' ? 'badge-warning' : 'badge-secondary'}`} style={ticket.status === 'amphora' ? { backgroundColor: '#8b5cf6', color: 'white' } : {}}>{ticket.status === 'open' ? 'Abierto' : ticket.status === 'amphora' ? 'En Amphora' : 'Cerrado'}</span></p>
                <p><strong>Prioridad:</strong> <span style={{ color: getPriorityColor(ticket.priority || 'Medium'), fontWeight: 'bold' }}>{ticket.priority || 'Medium'}</span></p>
                <p><strong>Fecha Objetivo:</strong> {new Date(ticket.target_date).toLocaleDateString()}</p>
                <p><strong>Pedido:</strong> {ticket.order_number || '-'}</p>
            </div>
            <div>
                <p><strong>Responsable:</strong> {ticket.responsible}</p>
                <p><strong>Creado por:</strong> {ticket.created_by_user?.username || 'Desconocido'}</p>
                <p><strong>Fecha Creación:</strong> {new Date(ticket.created_at).toLocaleString()}</p>
                {ticket.status === 'closed' && (
                    <p>
                        <strong>Notificado:</strong> {ticket.notified_at ? (
                            <span style={{ color: '#28a745', fontWeight: 'bold' }}>
                                ✓ {new Date(ticket.notified_at).toLocaleString()}
                            </span>
                        ) : (
                            <span style={{ color: '#dc3545' }}>Pendiente</span>
                        )}
                    </p>
                )}
            </div>
        </div>

        {/* Original Description */}
        <div style={{ border: '1px solid var(--border-color)', padding: '1rem', borderRadius: '8px' }}>
            <h4>Descripción Original</h4>
            <p style={{ whiteSpace: 'pre-wrap' }}>{ticket.description}</p>
            {ticket.image_data && (
                <div style={{ marginTop: '1rem' }}>
                    <img src={ticket.image_data} alt="Adjunto" style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px' }} />
                </div>
            )}
        </div>

        {/* Conversation History */}
        <div>
            <h4>Historial</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem', maxHeight: '400px', overflowY: 'auto' }}>
                {ticket.messages.length === 0 && <p className="text-secondary">No hay mensajes adicionales.</p>}
                {ticket.messages.map(msg => (
                    <div key={msg.id} style={{ 
                        backgroundColor: 'var(--bg-secondary)', 
                        padding: '1rem', 
                        borderRadius: '8px',
                        borderLeft: '4px solid var(--primary-color)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <strong>{msg.created_by_user?.username || 'Usuario'}</strong>
                            <small>{new Date(msg.created_at).toLocaleString()}</small>
                        </div>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</p>
                        {msg.image_data && (
                            <div style={{ marginTop: '0.5rem' }}>
                                <img src={msg.image_data} alt="Adjunto" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }} />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* New Message Input */}
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                <h5>Nueva Comunicación</h5>
                <textarea
                    className="form-control"
                    rows={3}
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    onPaste={onPaste}
                    placeholder="Escribe una actualización..."
                    style={{ marginBottom: '0.5rem' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input 
                            type="file" 
                            accept="image/*"
                            ref={fileInputRef}
                            onChange={e => e.target.files && handleImageUpload(e.target.files[0])}
                            style={{ display: 'none' }}
                        />
                        <button 
                            className="btn btn-secondary btn-sm"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            📷 Adjuntar Imagen
                        </button>
                        {newImage && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span className="text-success">Imagen lista</span>
                                <button className="btn btn-danger btn-xs" onClick={() => setNewImage(null)}>X</button>
                            </div>
                        )}
                    </div>
                    <button 
                        className="btn btn-primary" 
                        onClick={handleSendMessage}
                        disabled={sending || (!newMessage && !newImage)}
                    >
                        {sending ? 'Enviando...' : 'Enviar Actualización'}
                    </button>
                </div>
            </div>
        </div>
      </div>
      ) : null}
    </Modal>
  );
}
